import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { RiskFormModal } from '../features/risks/RiskFormModal';
import { SCORE_OPTIONS } from '../features/context/scoringUtils';
import {
  createRiskAction,
  createRiskAssessment,
  escalateRiskActionToNc,
  fetchRisk,
  linkRiskObjective,
  syncRiskOrigin,
  unlinkRiskObjective,
  updateRisk,
  updateRiskAction,
} from '../features/risks/risksApi';
import type { LinkedObjective, RiskAction, RiskAssessment, RiskEntry } from '../features/risks/types';
import { fetchObjectives } from '../features/objectives/objectivesApi';
import type { QualityObjective } from '../features/objectives/types';
import { OBJECTIVE_STATUS_LABEL } from '../features/objectives/objectiveUtils';
import { AttachmentsList } from '../components/AttachmentsList';
import {
  ACTION_STATUS_LABEL,
  ASSESSMENT_KIND_LABEL,
  LEVEL_LABEL_TEXT,
  RISK_KIND_LABEL,
  RISK_STATUS_LABEL,
  RISK_TREATMENT_LABEL,
  levelLabel,
} from '../features/risks/riskUtils';
import { DateCell } from '../components/DateCell';

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const message = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

export function RiskDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('risks.access');
  const { canView: canViewObjectives } = usePlatformAccessLevel('objectives.access');
  const [risk, setRisk] = useState<RiskEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [actionDescription, setActionDescription] = useState('');
  const [residualS, setResidualS] = useState(2);
  const [residualO, setResidualO] = useState(2);
  const [residualD, setResidualD] = useState(2);
  const [residualC, setResidualC] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [residualSubmitting, setResidualSubmitting] = useState(false);
  const [residualFeedback, setResidualFeedback] = useState<{
    type: 'error' | 'success';
    message: string;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [objectiveOptions, setObjectiveOptions] = useState<QualityObjective[]>([]);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
  const [linkingObjective, setLinkingObjective] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const objectivesPromise = canViewObjectives
        ? fetchObjectives({ pageSize: 200 }).catch(() => ({ data: [] as QualityObjective[] }))
        : Promise.resolve({ data: [] as QualityObjective[] });

      const [data, objectivesResult] = await Promise.all([fetchRisk(id), objectivesPromise]);
      setRisk(data);
      setObjectiveOptions(
        objectivesResult.data.filter((objective) => objective.status !== 'cancelado'),
      );
      const latestResidual = data.assessments?.find((a) => a.kind === 'residual');
      setResidualS(latestResidual?.severity ?? data.residualImpact ?? 2);
      setResidualO(latestResidual?.occurrence ?? data.residualProbability ?? 2);
      setResidualD(latestResidual?.detection ?? 2);
      setResidualC(latestResidual?.controlLevel ?? 3);
    } catch {
      setError('No se pudo cargar el riesgo.');
    } finally {
      setLoading(false);
    }
  }, [id, canViewObjectives]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkableObjectives = useMemo(() => {
    if (!risk) return [];
    const linkedIds = new Set((risk.objectives ?? []).map((objective) => objective.id));
    return objectiveOptions.filter(
      (objective) =>
        !linkedIds.has(objective.id) &&
        (!objective.relatedRiskId || objective.relatedRiskId === risk.id),
    );
  }, [risk, objectiveOptions]);

  const handleUpdate = async (payload: Parameters<typeof updateRisk>[1]) => {
    if (!id) return;
    await updateRisk(id, payload);
    await load();
  };

  const handleAddAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !actionDescription.trim()) return;
    setSubmitting(true);
    try {
      await createRiskAction(id, { description: actionDescription.trim() });
      setActionDescription('');
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const handleResidualAssess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !risk?.evaluationMethod) return;
    setResidualSubmitting(true);
    setResidualFeedback(null);
    try {
      const methodKind = risk.evaluationMethod.kind;
      await createRiskAssessment(id, {
        kind: 'residual',
        severity: residualS,
        occurrence: residualO,
        detection: methodKind === 'prob_impacto' ? null : residualD,
        controlLevel: methodKind === 'sodc' ? residualC : null,
      });
      await load();
      setResidualFeedback({
        type: 'success',
        message: 'Evaluación residual registrada. Revisá el historial y el nivel residual arriba.',
      });
    } catch (error) {
      setResidualFeedback({
        type: 'error',
        message: apiErrorMessage(
          error,
          'No se pudo registrar la evaluación residual. Verifique el método de evaluación del riesgo.',
        ),
      });
    } finally {
      setResidualSubmitting(false);
    }
  };

  const handleSyncOrigin = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      await syncRiskOrigin(id);
      await load();
    } catch {
      window.alert('No se pudo sincronizar con el origen.');
    } finally {
      setSyncing(false);
    }
  };

  const handleActionStatus = async (action: RiskAction, status: RiskAction['status']) => {
    await updateRiskAction(action.id, { status });
    await load();
  };

  const handleLinkObjective = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !selectedObjectiveId) return;
    setLinkingObjective(true);
    try {
      await linkRiskObjective(id, selectedObjectiveId);
      setSelectedObjectiveId('');
      await load();
    } finally {
      setLinkingObjective(false);
    }
  };

  const handleUnlinkObjective = async (objective: LinkedObjective) => {
    if (!id) return;
    await unlinkRiskObjective(id, objective.id);
    await load();
  };

  const handleEscalate = async (action: RiskAction) => {
    if (action.qmsRecordId) return;
    const ok = await confirm({
      title: 'Escalar a No Conformidad',
      message:
        'Se creará un expediente de no conformidad en QMS vinculado a esta acción de tratamiento. ¿Desea continuar?',
      confirmText: 'Escalar a NC',
      variant: 'danger',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await escalateRiskActionToNc(action.id);
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando riesgo…</p>;
  }

  if (error || !risk) {
    return <p className='text-sm text-destructive'>{error ?? 'Riesgo no encontrado.'}</p>;
  }

  const inherentLabel = levelLabel(risk.level);
  const residualLevel =
    risk.residualLevel ??
    (risk.residualProbability && risk.residualImpact
      ? risk.residualProbability * risk.residualImpact
      : null);
  const residualLabel = residualLevel !== null ? levelLabel(residualLevel) : null;
  const assessments = risk.assessments ?? [];
  const evaluationKind = risk.evaluationMethod?.kind;
  const usesSod = evaluationKind === 'sod' || evaluationKind === 'sodc';
  const usesSodc = evaluationKind === 'sodc';

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <Link to='/riesgos' className='text-sm text-primary hover:underline'>
            ← Volver al listado
          </Link>
          <div className='flex flex-wrap items-center gap-2 mt-2'>
            <h1 className='text-2xl font-bold'>
              {risk.code} — {risk.title}
            </h1>
            {risk.originOutOfSync && (
              <span className='text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded'>
                Desactualizado
              </span>
            )}
          </div>
          <p className='text-sm text-muted-foreground mt-1'>
            {RISK_KIND_LABEL[risk.kind]} · {RISK_STATUS_LABEL[risk.status]}
            {risk.evaluationMethod && ` · ${risk.evaluationMethod.name}`}
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          {canEdit && risk.originOutOfSync && (
            <button
              type='button'
              disabled={syncing}
              onClick={() => void handleSyncOrigin()}
              className='px-4 py-2 text-sm rounded-md border disabled:opacity-50'
            >
              {syncing ? 'Sincronizando…' : 'Sincronizar'}
            </button>
          )}
          {canEdit && (
            <button
              type='button'
              onClick={() => setEditOpen(true)}
              className='px-4 py-2 text-sm rounded-md border'
            >
              Editar
            </button>
          )}
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <section className='rounded-lg border p-4 space-y-2 text-sm'>
          <h2 className='font-semibold'>Datos generales</h2>
          <p>{risk.description}</p>
          {risk.processArea && (
            <p>
              <span className='text-muted-foreground'>Área:</span> {risk.processArea}
            </p>
          )}
          <p>
            <span className='text-muted-foreground'>Tratamiento:</span>{' '}
            {RISK_TREATMENT_LABEL[risk.treatment]}
          </p>
          {risk.sourceSwot && (
            <p>
              <span className='text-muted-foreground'>Origen FODA:</span>{' '}
              [{risk.sourceSwot.category}] {risk.sourceSwot.description}
            </p>
          )}
          {risk.sourceParty && (
            <p>
              <span className='text-muted-foreground'>Parte interesada:</span> {risk.sourceParty.name}
            </p>
          )}
          {risk.sourceContext && (
            <p>
              <span className='text-muted-foreground'>Cambio de contexto:</span>{' '}
              {risk.sourceContext.situation}
            </p>
          )}
          {risk.sourceFinding && (
            <p>
              <span className='text-muted-foreground'>Hallazgo de auditoría:</span>{' '}
              {risk.sourceFinding.clause ?? risk.sourceFinding.type} —{' '}
              {risk.sourceFinding.description.slice(0, 120)}
            </p>
          )}
          {risk.dueDate && (
            <p>
              <span className='text-muted-foreground'>Vencimiento:</span>{' '}
              <DateCell value={risk.dueDate} />
            </p>
          )}
        </section>

        <section className='rounded-lg border p-4 space-y-2 text-sm'>
          <h2 className='font-semibold'>Evaluación</h2>
          <p>
            Inherente: P={risk.probability} × I={risk.impact} ={' '}
            <strong>
              {risk.level} ({LEVEL_LABEL_TEXT[inherentLabel]})
            </strong>
          </p>
          {residualLevel !== null && residualLabel && (
            <p>
              Residual: P={risk.residualProbability} × I={risk.residualImpact} ={' '}
              <strong>
                {residualLevel} ({LEVEL_LABEL_TEXT[residualLabel]})
              </strong>
            </p>
          )}
          {canEdit && (
            <form onSubmit={handleResidualAssess} className='mt-3 space-y-2 border-t pt-3'>
              <p className='text-xs text-muted-foreground'>
                Registrar evaluación residual tras las acciones de tratamiento. Crea una entrada en el
                historial y actualiza el nivel residual del riesgo.
              </p>
              {!risk.evaluationMethod ? (
                <p className='text-sm text-amber-700'>
                  Este riesgo no tiene método de evaluación asignado. Editá el riesgo y elegí uno antes
                  de evaluar residual.
                </p>
              ) : (
                <>
                  <p className='text-xs text-muted-foreground'>
                    Método: <strong>{risk.evaluationMethod.name}</strong>
                    {evaluationKind === 'prob_impacto' && ' (probabilidad × impacto)'}
                    {evaluationKind === 'sod' && ' (severidad × ocurrencia × detección)'}
                    {evaluationKind === 'sodc' && ' (S×O×D degradado por nivel de control)'}
                  </p>
                  <div
                    className={`grid gap-2 ${usesSodc ? 'grid-cols-4' : usesSod ? 'grid-cols-3' : 'grid-cols-2'}`}
                  >
                    <label className='text-xs'>
                      {evaluationKind === 'prob_impacto' ? 'I (impacto)' : 'S'}
                      <select
                        value={residualS}
                        onChange={(e) => setResidualS(Number(e.target.value))}
                        className='block w-full border rounded px-2 py-1 mt-1'
                      >
                        {SCORE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className='text-xs'>
                      {evaluationKind === 'prob_impacto' ? 'P (prob.)' : 'O'}
                      <select
                        value={residualO}
                        onChange={(e) => setResidualO(Number(e.target.value))}
                        className='block w-full border rounded px-2 py-1 mt-1'
                      >
                        {SCORE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    {usesSod && (
                      <label className='text-xs'>
                        D
                        <select
                          value={residualD}
                          onChange={(e) => setResidualD(Number(e.target.value))}
                          className='block w-full border rounded px-2 py-1 mt-1'
                        >
                          {SCORE_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {usesSodc && (
                      <label className='text-xs'>
                        C
                        <select
                          value={residualC}
                          onChange={(e) => setResidualC(Number(e.target.value))}
                          className='block w-full border rounded px-2 py-1 mt-1'
                        >
                          {SCORE_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                  {residualFeedback && (
                    <p
                      className={`text-sm ${residualFeedback.type === 'error' ? 'text-destructive' : 'text-green-700'}`}
                      role={residualFeedback.type === 'error' ? 'alert' : 'status'}
                    >
                      {residualFeedback.message}
                    </p>
                  )}
                  <button
                    type='submit'
                    disabled={residualSubmitting}
                    className='px-3 py-1 text-sm rounded bg-primary text-primary-foreground disabled:opacity-50'
                  >
                    {residualSubmitting ? 'Guardando…' : 'Evaluar residual'}
                  </button>
                </>
              )}
            </form>
          )}
        </section>
      </div>

      <section className='rounded-lg border p-4'>
        <h2 className='font-semibold mb-3'>Historial de evaluaciones</h2>
        {assessments.length === 0 ? (
          <p className='text-sm text-muted-foreground'>Sin evaluaciones registradas.</p>
        ) : (
          <ul className='space-y-2'>
            {assessments.map((assessment: RiskAssessment) => (
              <li key={assessment.id} className='text-sm border rounded-md p-3'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='font-medium'>
                    {ASSESSMENT_KIND_LABEL[assessment.kind] ?? assessment.kind}
                  </span>
                  <span className='text-muted-foreground'>
                    S={assessment.severity} · O={assessment.occurrence}
                    {assessment.detection != null && ` · D=${assessment.detection}`}
                    {assessment.controlLevel != null && ` · C=${assessment.controlLevel}`}
                  </span>
                  <span className='text-muted-foreground'>
                    → {assessment.rawValue} ({assessment.levelLabel})
                  </span>
                </div>
                <p className='text-xs text-muted-foreground mt-1'>
                  <DateCell value={assessment.assessedAt} />
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className='rounded-lg border p-4'>
        <h2 className='font-semibold mb-3'>Objetivos de calidad vinculados</h2>
        {(risk.objectives ?? []).length === 0 ? (
          <p className='text-sm text-muted-foreground mb-3'>Sin objetivos vinculados.</p>
        ) : (
          <ul className='space-y-2 mb-4'>
            {(risk.objectives ?? []).map((objective) => (
              <li
                key={objective.id}
                className='flex flex-wrap items-center justify-between gap-2 text-sm border rounded-md p-3'
              >
                <Link to={`/objetivos/${objective.id}`} className='text-primary hover:underline'>
                  {objective.code} — {objective.title}
                </Link>
                {canEdit && (
                  <button
                    type='button'
                    onClick={() => void handleUnlinkObjective(objective)}
                    className='text-xs text-destructive hover:underline'
                  >
                    Desvincular
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && !canViewObjectives && (
          <p className='text-sm text-muted-foreground'>
            Necesitás permiso de visualización en Objetivos para vincular objetivos desde acá.
          </p>
        )}
        {canEdit && canViewObjectives && (
          <form onSubmit={handleLinkObjective} className='flex flex-wrap gap-2'>
            <select
              value={selectedObjectiveId}
              onChange={(e) => setSelectedObjectiveId(e.target.value)}
              className='flex-1 min-w-[200px] border rounded-md px-3 py-2 text-sm'
            >
              <option value=''>Seleccionar objetivo…</option>
              {linkableObjectives.map((objective) => (
                <option key={objective.id} value={objective.id}>
                  {objective.code} — {objective.title}
                  {objective.status !== 'activo'
                    ? ` (${OBJECTIVE_STATUS_LABEL[objective.status]})`
                    : ''}
                </option>
              ))}
            </select>
            <button
              type='submit'
              disabled={linkingObjective || !selectedObjectiveId}
              className='px-4 py-2 text-sm rounded-md border disabled:opacity-50'
            >
              Vincular
            </button>
          </form>
        )}
        {canEdit && canViewObjectives && linkableObjectives.length === 0 && objectiveOptions.length > 0 && (
          <p className='text-sm text-muted-foreground mt-2'>
            Todos los objetivos disponibles ya están vinculados a este u otro riesgo.
          </p>
        )}
        {canEdit && canViewObjectives && objectiveOptions.length === 0 && (
          <p className='text-sm text-muted-foreground mt-2'>
            Creá objetivos en la sección Objetivos para poder vincularlos acá.
          </p>
        )}
      </section>

      <section className='rounded-lg border p-4'>
        <AttachmentsList resourceType='risk' resourceId={risk.id} readOnly={!canEdit} />
      </section>

      <section className='rounded-lg border p-4'>
        <h2 className='font-semibold mb-3'>Acciones de tratamiento</h2>
        {canEdit && (
          <form onSubmit={handleAddAction} className='flex gap-2 mb-4'>
            <input
              type='text'
              value={actionDescription}
              onChange={(e) => setActionDescription(e.target.value)}
              placeholder='Descripción de la acción…'
              className='flex-1 border rounded-md px-3 py-2 text-sm'
              maxLength={10000}
            />
            <button
              type='submit'
              disabled={submitting || !actionDescription.trim()}
              className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
            >
              Agregar
            </button>
          </form>
        )}
        <ul className='space-y-3'>
          {(risk.actions ?? []).length === 0 && (
            <li className='text-sm text-muted-foreground'>Sin acciones registradas.</li>
          )}
          {(risk.actions ?? []).map((action) => (
            <li key={action.id} className='rounded-md border p-3 text-sm'>
              <p>{action.description}</p>
              <div className='mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
                <span>{ACTION_STATUS_LABEL[action.status]}</span>
                {action.dueDate && (
                  <span>
                    Vence: <DateCell value={action.dueDate} />
                  </span>
                )}
                {action.qmsRecordId && (
                  <Link
                    to={`/registros/no-conformidades/${action.qmsRecordId}`}
                    className='text-primary hover:underline'
                  >
                    NC vinculada
                  </Link>
                )}
              </div>
              {canEdit && (
                <div className='mt-2 flex flex-wrap gap-2'>
                  {action.status !== 'en_progreso' && action.status !== 'completada' && (
                    <button
                      type='button'
                      onClick={() => handleActionStatus(action, 'en_progreso')}
                      className='text-xs text-primary hover:underline'
                    >
                      En progreso
                    </button>
                  )}
                  {action.status !== 'completada' && (
                    <button
                      type='button'
                      onClick={() => handleActionStatus(action, 'completada')}
                      className='text-xs text-primary hover:underline'
                    >
                      Completar
                    </button>
                  )}
                  <button
                    type='button'
                    disabled={!!action.qmsRecordId || submitting}
                    onClick={() => handleEscalate(action)}
                    className='text-xs text-destructive hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                    title={
                      action.qmsRecordId
                        ? 'Ya escalada a NC'
                        : 'Crear expediente de no conformidad'
                    }
                  >
                    Escalar a NC
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {canEdit && (
        <RiskFormModal
          open={editOpen}
          onOpenChange={setEditOpen}
          initial={risk}
          onSubmit={handleUpdate}
        />
      )}
    </div>
  );
}

export default RiskDetailPage;
